/* eslint-disable @typescript-eslint/naming-convention */
import { Component, Input, EventEmitter, Output } from '@angular/core';
import { ControlValueAccessor } from '@angular/forms';
import { TimeModel } from '../../models/time.model';
import { meridiansEnum } from '../../enums/meridians.enum';
import { TwentyFourHourModel } from '../../models/twenty-four-hour.model';
import { MinuteModel } from '../../models/minute.model';
import { TwelveHourModel } from '../../models/twelve-hour.model';
import { timeUnitsEnum } from '../../enums/time-units.enum';
import { timeFormatsEnum } from '../../../../Core/Users/enums/time-formats.enum';
import { getProvider } from '../../models/control-value-accessor.model';
import { ButtonTypesEnum } from '../../enums/button-types.enum';

export const timePickerComponentText = {
    AM: 'AM',
    PM: 'PM',
    selectTime: 'Select time',
    selectHour: 'Select hour',
    selectMinute: 'Select minute',
    selectMeridian: 'Select meridian',
    timeZone: 'Timezone'
};

/**
 * ### Accessibility Usage
 * + ***hourDescribedBy*** - use this to attach error messages to each hour control. Use message's id
 * + ***id*** - provide an unique id so that this component can be correctly recognized by screen readers. Use interpolation [id]="'unique_id'"
 * + ***isHiddenLabel*** - set to true to hide legend from UI. Default is true
 * + ***minuteDescribedBy*** - use this to attach error messages to each minute control. Use message's id
 */
@Component({
    selector: 'agpr-multi-time-picker',
    templateUrl: './multi-time-picker.component.html',
    styleUrls: ['./multi-time-picker.component.scss'],
    providers: [getProvider(MultiTimePickerComponent)],
    standalone: false
})
export class MultiTimePickerComponent implements ControlValueAccessor {
    readonly buttonTypes = ButtonTypesEnum;

    @Input() id: string = null;
    @Input() localization = timePickerComponentText;
    @Input() header: string = null;
    @Input() isHiddenLabel: boolean = true;
    @Input() showTimeZoneSelector: boolean = true;
    @Input() disabled: boolean = false;
    @Input() styleClass: string | object = null;
    @Input() hourDescribedBy: string = null;
    @Input() minuteDescribedBy: string = null;
    @Input() infoIconStyleClass: string = null;
    @Input() infoTooltipStyleClass: string = null;
    @Input() showTimezoneDisclaimer: boolean = true;

    @Output() changeHour: EventEmitter<TwelveHourModel | TwentyFourHourModel> = new EventEmitter<TwelveHourModel | TwentyFourHourModel>();
    @Output() changeMinute: EventEmitter<MinuteModel> = new EventEmitter<MinuteModel>();
    @Output() changeTimeZone: EventEmitter<string> = new EventEmitter<string>();

    selectedTimeSeries: TimeModel[] = null;
    meridians = meridiansEnum;
    timeFormats = timeFormatsEnum;
    minHour: TwelveHourModel | TwentyFourHourModel = 1;
    maxHour: TwelveHourModel | TwentyFourHourModel = 12;
    minMinute: MinuteModel = 0;
    maxMinute: MinuteModel = 59;
    timeUnits = timeUnitsEnum;
    activeTimeUnit: timeUnitsEnum = null;
    hourFocused: boolean = false;

    /**
     * Class constructor
     */
    constructor() {}

    /**
     * Method required by the ControlValueAccessor
     *
     * to identify when the model has changed
     */
    onModelChange: Function = () => {};

    /**
     * Method required by the ControlValueAccessor
     *
     * to identify when the input has been touched
     */
    onModelTouched: Function = () => {};

    /**
     * Required as part of ControlValueAccessor
     *
     * Adds a new value to the the expression passed to the ngModel property on the component
     */
    writeValue(value: any): void {
        if (value !== null) {
            this.selectedTimeSeries = value;
        }
    }

    /**
     * Required as part of ControlValueAccessor
     *
     * sets up a way to know when the model has changed
     */
    registerOnChange(fn: Function): void {
        this.onModelChange = fn;
    }

    /**
     * Required as part of ControlValueAccessor
     * sets up a way to know when the input has been touched
     */
    registerOnTouched(fn: Function): void {
        this.onModelTouched = fn;
    }

    /**
     * Fires an event on the component when the time zone is changed
     */
    timeZoneChanged(event, index): void {
        // We need to update the TZ values for every element in the series as TZ is not maintained independently
        for (const value of this.selectedTimeSeries) {
            value.timeZone = event;
        }
        this.onModelChange(this.selectedTimeSeries);
    }

    /**
     * Removes a series from seriesList
     *
     * @param index - index of series to remove
     */
    removeSeries(index: number): void {
        this.selectedTimeSeries.splice(index, 1);
        this.onModelChange(this.selectedTimeSeries);
    }

    /**
     * Adds a new series for multi-series blocks
     */
    addSeries(): void {
        // Create a new entry, cloning the time format and time zone as these remain constant across the series
        const newEntry = new TimeModel();
        newEntry.format = this.selectedTimeSeries[0].format;
        newEntry.timeZone = this.selectedTimeSeries[0].timeZone;
        this.selectedTimeSeries.push(newEntry);

        this.onModelChange(this.selectedTimeSeries);
    }
}
